import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import api from '../api/client';
import { getSocket } from '../api/socket';
import './Messages.css';

function formatTime(date) {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function otherParticipant(discussion, myPublicId) {
    const others = (discussion.participants || []).filter((p) => p.publicId !== myPublicId);
    return others[0] || discussion.participants?.[0] || null;
}

export default function Messages({ translations }) {
    const { discussionId } = useParams();
    const { user } = useAuth();

    const [discussions, setDiscussions] = useState([]);
    const [loadingList, setLoadingList] = useState(true);

    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [activeDiscussion, setActiveDiscussion] = useState(null);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [unreadMap, setUnreadMap] = useState({});

    const bottomRef = useRef(null);
    const activeIdRef = useRef(null);
    useEffect(() => { activeIdRef.current = discussionId || null; }, [discussionId]);
    const t = (key, fallback) => translations?.[key] || fallback;

    // --- Load discussion list ---
    useEffect(() => {
        let active = true;
        api.getDiscussions(0, 50)
            .then((res) => {
                if (!active) return;
                const list = res?.discussions || [];
                setDiscussions(list);
                // Fetch per-conversation unread counts for the list badges.
                Promise.all(
                    list.map((d) =>
                        api.getDiscussionUnreadCount(d.id)
                            .then((r) => [d.id, r?.count || 0])
                            .catch(() => [d.id, 0]),
                    ),
                ).then((entries) => {
                    if (active) setUnreadMap(Object.fromEntries(entries));
                });
            })
            .catch(() => {})
            .finally(() => { if (active) setLoadingList(false); });
        return () => { active = false; };
    }, []);

    // --- Global socket: keep the discussion list fresh ---
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return undefined;

        const onNotification = (payload) => {
            setDiscussions((prev) => {
                const idx = prev.findIndex((d) => d.id === payload.discussionId);
                if (idx === -1) return prev;
                const updated = {
                    ...prev[idx],
                    lastMessagePreview: payload.lastMessagePreview,
                    lastMessageAt: payload.lastMessageAt,
                };
                const next = [...prev];
                next.splice(idx, 1);
                return [updated, ...next];
            });
        };

        // A message landed in one of our conversations (we are a recipient).
        const onReceived = (payload) => {
            const did = payload?.discussionId;
            if (!did || did === activeIdRef.current) return; // active convo is auto-read
            setUnreadMap((prev) => ({ ...prev, [did]: (prev[did] || 0) + 1 }));
        };

        socket.on('discussion:notification', onNotification);
        socket.on('message:received', onReceived);
        return () => {
            socket.off('discussion:notification', onNotification);
            socket.off('message:received', onReceived);
        };
    }, []);

    // --- Load active conversation + join its socket room ---
    const loadMessages = useCallback(async (id) => {
        setLoadingMessages(true);
        try {
            const [msgRes, disc] = await Promise.all([
                api.getMessages(id, 0, 50),
                api.getDiscussion(id).catch(() => null),
            ]);
            // API returns newest-first; show oldest-first.
            const list = (msgRes?.messages || []).slice().reverse();
            setMessages(list);
            setActiveDiscussion(disc);
        } catch {
            setMessages([]);
            setActiveDiscussion(null);
        } finally {
            setLoadingMessages(false);
        }
    }, []);

    useEffect(() => {
        if (!discussionId) {
            setActiveDiscussion(null);
            setMessages([]);
            return undefined;
        }
        loadMessages(discussionId);

        // Opening a conversation marks it read.
        api.markDiscussionRead(discussionId).catch(() => {});
        setUnreadMap((prev) => (prev[discussionId] ? { ...prev, [discussionId]: 0 } : prev));
        window.dispatchEvent(new Event('dm:changed'));

        const socket = getSocket();
        if (!socket) return undefined;

        socket.emit('discussion:join', { discussionId });

        const onNew = (message) => {
            if (message.discussionId !== discussionId) return;
            setMessages((prev) => {
                if (prev.some((m) => m.id === message.id)) return prev;
                return [...prev, message];
            });
            // We're viewing this conversation, so keep it marked read.
            if (message.sender?.publicId !== user?.publicId) {
                api.markDiscussionRead(discussionId).catch(() => {});
                window.dispatchEvent(new Event('dm:changed'));
            }
        };
        const onUpdated = (message) => {
            if (message.discussionId !== discussionId) return;
            setMessages((prev) => prev.map((m) => (m.id === message.id ? message : m)));
        };
        const onDeleted = ({ discussionId: did, messageId }) => {
            if (did !== discussionId) return;
            setMessages((prev) => prev.filter((m) => m.id !== messageId));
        };

        socket.on('message:new', onNew);
        socket.on('message:updated', onUpdated);
        socket.on('message:deleted', onDeleted);

        return () => {
            socket.emit('discussion:leave', { discussionId });
            socket.off('message:new', onNew);
            socket.off('message:updated', onUpdated);
            socket.off('message:deleted', onDeleted);
        };
    }, [discussionId, loadMessages, user]);

    // --- Auto-scroll to the newest message ---
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        const value = text.trim();
        if (!value || sending || !discussionId) return;
        setSending(true);
        try {
            const message = await api.sendMessage(discussionId, { type: 'text', content: value });
            setText('');
            // Add immediately (socket echo is de-duplicated by id).
            setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
            // Bump this discussion to the top of the list with the new preview.
            setDiscussions((prev) => {
                const idx = prev.findIndex((d) => d.id === discussionId);
                if (idx === -1) return prev;
                const updated = { ...prev[idx], lastMessagePreview: value, lastMessageAt: new Date().toISOString() };
                const next = [...prev];
                next.splice(idx, 1);
                return [updated, ...next];
            });
        } catch {
            // Ignore send errors
        } finally {
            setSending(false);
        }
    };

    const startEdit = (m) => {
        setEditingId(m.id);
        setEditText(m.content || '');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditText('');
    };

    const saveEdit = async (m) => {
        const value = editText.trim();
        if (!value) return;
        try {
            const updated = await api.editMessage(discussionId, m.id, value);
            setMessages((prev) => prev.map((x) => (x.id === m.id ? (updated || { ...x, content: value, isEdited: true }) : x)));
        } catch {
            // Ignore
        } finally {
            cancelEdit();
        }
    };

    const deleteMessage = async (m) => {
        if (!window.confirm(t('messages-delete-confirm', 'Delete this message?'))) return;
        try {
            await api.deleteMessage(discussionId, m.id);
            setMessages((prev) => prev.filter((x) => x.id !== m.id));
        } catch {
            // Ignore
        }
    };

    const activeOther = activeDiscussion ? otherParticipant(activeDiscussion, user?.publicId) : null;

    return (
        <>
            <Header translations={translations} />
            <div className={`messages-layout ${discussionId ? 'has-active' : ''}`}>
                {/* Left: discussions list */}
                <aside className="messages-sidebar">
                    <h2 className="messages-sidebar-title">{t('messages-title', 'Messages')}</h2>
                    {loadingList ? (
                        <div className="messages-loading"><div className="spinner" /></div>
                    ) : discussions.length === 0 ? (
                        <p className="messages-empty">{t('messages-empty', 'No conversations yet.')}</p>
                    ) : (
                        <ul className="messages-discussion-list">
                            {discussions.map((d) => {
                                const other = otherParticipant(d, user?.publicId);
                                const isActive = d.id === discussionId;
                                return (
                                    <li key={d.id}>
                                        <Link
                                            to={`/messages/${d.id}`}
                                            className={`messages-discussion-item ${isActive ? 'active' : ''}`}
                                        >
                                            {other?.profilePicture ? (
                                                <img src={other.profilePicture} alt="" className="messages-avatar" />
                                            ) : (
                                                <span className="messages-avatar messages-avatar-fallback">
                                                    {(other?.username || '?')[0].toUpperCase()}
                                                </span>
                                            )}
                                            <div className="messages-discussion-meta">
                                                <span className="messages-discussion-name">
                                                    {d.name || other?.username || 'Unknown'}
                                                </span>
                                                <span className="messages-discussion-preview">
                                                    {d.lastMessagePreview || t('messages-no-messages', 'No messages yet')}
                                                </span>
                                            </div>
                                            <span className="messages-discussion-time">
                                                {formatTime(d.lastMessageAt)}
                                                {unreadMap[d.id] > 0 && !isActive && (
                                                    <span className="messages-unread-badge">
                                                        {unreadMap[d.id] > 99 ? '99+' : unreadMap[d.id]}
                                                    </span>
                                                )}
                                            </span>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </aside>

                {/* Right: active conversation */}
                <section className="messages-conversation">
                    {!discussionId ? (
                        <div className="messages-placeholder">
                            <p>{t('messages-select', 'Select a conversation to start chatting.')}</p>
                        </div>
                    ) : (
                        <>
                            <header className="messages-conversation-header">
                                <Link to="/messages" className="messages-back" aria-label="Back">‹</Link>
                                {activeOther && (
                                    <Link to={`/user/${activeOther.publicId}`} className="messages-conversation-user">
                                        {activeOther.profilePicture ? (
                                            <img src={activeOther.profilePicture} alt="" className="messages-avatar" />
                                        ) : (
                                            <span className="messages-avatar messages-avatar-fallback">
                                                {(activeOther.username || '?')[0].toUpperCase()}
                                            </span>
                                        )}
                                        <span>{activeDiscussion.name || activeOther.username || 'Unknown'}</span>
                                    </Link>
                                )}
                            </header>

                            <div className="messages-thread">
                                {loadingMessages ? (
                                    <div className="messages-loading"><div className="spinner" /></div>
                                ) : messages.length === 0 ? (
                                    <p className="messages-empty">{t('messages-say-hi', 'Say hi! 👋')}</p>
                                ) : (
                                    messages.map((m) => {
                                        const mine = m.sender?.publicId === user?.publicId;
                                        const editing = editingId === m.id;
                                        return (
                                            <div key={m.id} className={`message-row ${mine ? 'mine' : 'theirs'}`}>
                                                {mine && m.type === 'text' && !editing && (
                                                    <div className="message-actions">
                                                        <button type="button" onClick={() => startEdit(m)} title={t('messages-edit', 'Edit')}>✎</button>
                                                        <button type="button" onClick={() => deleteMessage(m)} title={t('messages-delete', 'Delete')}>🗑</button>
                                                    </div>
                                                )}
                                                <div className="message-bubble">
                                                    {editing ? (
                                                        <div className="message-edit">
                                                            <textarea
                                                                value={editText}
                                                                onChange={(e) => setEditText(e.target.value)}
                                                                rows={2}
                                                                maxLength={5000}
                                                                autoFocus
                                                            />
                                                            <div className="message-edit-actions">
                                                                <button type="button" onClick={() => saveEdit(m)}>{t('messages-save', 'Save')}</button>
                                                                <button type="button" className="ghost" onClick={cancelEdit}>{t('messages-cancel', 'Cancel')}</button>
                                                            </div>
                                                        </div>
                                                    ) : m.type === 'text' ? (
                                                        <span className="message-content">{m.content}</span>
                                                    ) : m.type === 'images' ? (
                                                        <div className="message-images">
                                                            {(m.attachments || []).map((a, i) => (
                                                                <img key={i} src={a.url} alt="" className="message-image" />
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="message-content">[Audio]</span>
                                                    )}
                                                    {!editing && (
                                                        <span className="message-time">
                                                            {formatTime(m.createdAt)}{m.isEdited ? ` · ${t('messages-edited', 'edited')}` : ''}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={bottomRef} />
                            </div>

                            <form className="messages-composer" onSubmit={handleSend}>
                                <input
                                    type="text"
                                    className="messages-input"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={t('messages-placeholder', 'Write a message...')}
                                    maxLength={5000}
                                />
                                <button type="submit" className="messages-send" disabled={sending || !text.trim()}>
                                    {t('messages-send', 'Send')}
                                </button>
                            </form>
                        </>
                    )}
                </section>
            </div>
        </>
    );
}
